import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LoginMe,
  ForgotPassword,
  ResetPassword,
  VerifyResetLink,
  CompleteResetLink,
  ResetLinkInfo,
} from "../api/login";
import MainContext from "../context/MainContext";
import NotificationToast from "src/Notification/NotificationToast";
import "./login.css";

// "link" = /reset-password?token=… from the emailed one-time link (GZ#4917).
type View = "login" | "forgot" | "reset" | "link";

type ScheduleRow = {
  time: string;
  kind: string;
  name: string;
  status: string;
  type: "event" | "booking" | "closed" | "lesson";
};

const SCHEDULE_ROWS: ScheduleRow[] = [
  { time: "09:00", kind: "Group", name: "Morning class", status: "Confirmed", type: "event" },
  { time: "10:30", kind: "Private", name: "J. Bauer · 60 min", status: "Paid", type: "booking" },
  { time: "12:00", kind: "Block", name: "Maintenance", status: "Internal", type: "closed" },
  { time: "14:00", kind: "Session", name: "Members · venue B", status: "3 / 8", type: "lesson" },
];

const MailIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 6h16v12H4z" />
    <path d="m4 7 8 6 8-6" />
  </svg>
);

const LockIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="11" width="16" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5M11 19l-7-7 7-7" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const EyeOpenIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a18.16 18.16 0 0 1 4.06-4.94" />
    <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c6.5 0 10 7 10 7a18.5 18.5 0 0 1-3.06 3.94" />
    <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

type FieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  icon: React.ReactNode;
  trailing?: React.ReactNode;
  password?: boolean;
  masked?: boolean;
  onToggleMask?: () => void;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  inputMode?: "numeric" | "text";
  maxLength?: number;
  inputStyle?: React.CSSProperties;
  type?: string;
};

function LeField({
  id,
  label,
  value,
  onChange,
  icon,
  trailing,
  password,
  masked,
  onToggleMask,
  placeholder,
  autoComplete,
  autoFocus,
  inputMode,
  maxLength,
  inputStyle,
  type,
}: FieldProps) {
  const [focused, setFocused] = React.useState(false);
  const resolvedType = password
    ? masked === false
      ? "text"
      : "password"
    : type || "text";
  return (
    <label className="le-field" htmlFor={id}>
      <div className="le-field-head">
        <span className="le-field-label">{label}</span>
        {trailing}
      </div>
      <div className={`le-input${focused ? " focused" : ""}`}>
        <span className="le-input-icon">{icon}</span>
        <input
          id={id}
          name={id}
          type={resolvedType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          inputMode={inputMode}
          maxLength={maxLength}
          style={inputStyle}
          required
        />
        {onToggleMask && (
          <button
            type="button"
            className="le-eye"
            onClick={onToggleMask}
            aria-label={masked ? "Show password" : "Hide password"}
            tabIndex={-1}
          >
            {masked ? <EyeOpenIcon /> : <EyeOffIcon />}
          </button>
        )}
      </div>
    </label>
  );
}

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  // GZ#4917 — emailed one-time link: /reset-password?token=…
  const isResetLinkPage = location.pathname === "/reset-password";
  const linkToken = isResetLinkPage
    ? new URLSearchParams(location.search).get("token") || ""
    : "";
  const context = React.useContext(MainContext);
  const authenticated = context?.authenticated;
  const setAuthenticated = context?.setAuthenticated;
  const { setNotification } = React.useContext(MainContext);

  const [view, setView] = React.useState<View>(isResetLinkPage ? "link" : "login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [isMasked, setIsMasked] = React.useState(true);
  // GZ#3082: this used to be write-only state — rendered, toggled, and read
  // by nothing. It now reaches /users/login as `keep_signed_in` and decides
  // whether the session lasts 30 days or the ordinary 7.
  const [keepSignedIn, setKeepSignedIn] = React.useState(true);

  const [resetEmail, setResetEmail] = React.useState("");
  const [resetCode, setResetCode] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [newPasswordMasked, setNewPasswordMasked] = React.useState(true);
  const [confirmPasswordMasked, setConfirmPasswordMasked] = React.useState(true);

  // GZ#4917 — state of the emailed link: checking → valid (info) or invalid.
  const [linkInfo, setLinkInfo] = React.useState<ResetLinkInfo | null>(null);
  const [linkStatus, setLinkStatus] = React.useState<"checking" | "valid" | "invalid">("checking");
  const [linkError, setLinkError] = React.useState("");

  const showNotification = (message: string, type: "success" | "error") => {
    setNotification({ isShow: true, message, subMessage: "", type });
  };

  const clearResetState = () => {
    setView("login");
    setResetEmail("");
    setResetCode("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleMutationError = (error: any, fallback: string) => {
    showNotification(error?.response?.data?.message || fallback, "error");
  };

  const pendingMcpRequest = new URLSearchParams(window.location.search).get('mcp_request');
  const loginDestination = pendingMcpRequest && /^[A-Za-z0-9_-]{43}$/.test(pendingMcpRequest)
    ? `/mcp-connection?request=${encodeURIComponent(pendingMcpRequest)}` : '/calendars';

  // Auto-login from staging redirect (token passed via URL)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const autoToken = params.get("auto_token");
    if (autoToken) {
      try {
        const { jwtDecode } = require("jwt-decode");
        const decoded: any = jwtDecode(autoToken);
        if (decoded.exp * 1000 > Date.now()) {
          localStorage.setItem("accessToken", autoToken);
          const userData = {
            id: decoded.id,
            role: { name: decoded.role_id === 1 ? "SUPER_ADMIN" : "CLUB_ADMIN" },
            club_id: decoded.club_id,
          };
          localStorage.setItem("user", JSON.stringify(userData));
          if (setAuthenticated) setAuthenticated(true);
          window.history.replaceState({}, "", "/calendars");
          navigate(loginDestination, { replace: true });
          setTimeout(() => window.location.reload(), 500);
          return;
        }
      } catch (e) {
        console.error("Auto-login failed:", e);
      }
    }
  }, []);

  React.useEffect(() => {
    // The emailed reset link must open even when someone is signed in here.
    if (authenticated && !isResetLinkPage) {
      navigate(loginDestination, { replace: true });
    }
  }, [authenticated, navigate, loginDestination, isResetLinkPage]);

  // GZ#4917 — check the emailed link once on load.
  React.useEffect(() => {
    if (!isResetLinkPage) return;
    if (!linkToken) {
      setLinkStatus("invalid");
      setLinkError("This link has expired or was already used.");
      return;
    }
    let cancelled = false;
    setLinkStatus("checking");
    VerifyResetLink({ token: linkToken })
      .then((info) => {
        if (cancelled) return;
        setLinkInfo(info);
        setLinkStatus("valid");
      })
      .catch((error: any) => {
        if (cancelled) return;
        setLinkError(
          error?.response?.data?.message || "This link has expired or was already used."
        );
        setLinkStatus("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [isResetLinkPage, linkToken]);

  React.useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setViewportHeight();
    window.addEventListener("resize", setViewportHeight);
    return () => window.removeEventListener("resize", setViewportHeight);
  }, []);

  const loginMutation = useMutation({
    mutationFn: LoginMe,
    onSuccess: (data: any) => {
      showNotification("Successfully signed in", "success");
      if (data?.accessToken) {
        localStorage.setItem("accessToken", data?.accessToken);
        localStorage.setItem("user", JSON.stringify(data?.user));
        localStorage.setItem(
          "location_restriction",
          JSON.stringify(data?.user?.location_restriction) || "false"
        );
        if (data?.user?.role?.name === "STAFF") {
          localStorage.setItem("permission", JSON.stringify(data?.permission));
        }
        if (setAuthenticated) setAuthenticated(true);
        navigate(loginDestination, { replace: true });
        setTimeout(() => window.location.reload(), 1500);
      }
    },
    onError: (error: any) => handleMutationError(error, "Login failed"),
  });

  const forgotMutation = useMutation({
    mutationFn: ForgotPassword,
    onSuccess: () => {
      showNotification("Reset code sent to your email", "success");
      setView("reset");
    },
    onError: (error: any) => handleMutationError(error, "Failed to send reset code"),
  });

  const resetMutation = useMutation({
    mutationFn: ResetPassword,
    onSuccess: () => {
      showNotification("Password reset successfully", "success");
      clearResetState();
    },
    onError: (error: any) => handleMutationError(error, "Failed to reset password"),
  });

  const completeLinkMutation = useMutation({
    mutationFn: CompleteResetLink,
    onSuccess: () => {
      showNotification("Password set. Sign in with your new password.", "success");
      setNewPassword("");
      setConfirmPassword("");
      setEmail(linkInfo?.email || "");
      setView("login");
      navigate("/signin", { replace: true });
    },
    onError: (error: any) => handleMutationError(error, "Failed to set password"),
  });

  const handleLinkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showNotification("Passwords do not match", "error");
      return;
    }
    if (newPassword.length < 6) {
      showNotification("Password must be at least 6 characters", "error");
      return;
    }
    completeLinkMutation.mutate({ token: linkToken, newPassword });
  };

  const backToSignIn = () => {
    setView("login");
    navigate("/signin", { replace: true });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    loginMutation.mutate({ email, password, keepSignedIn });
  };

  const handleForgotSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    forgotMutation.mutate({ email: resetEmail });
  };

  const handleResetSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showNotification("Passwords do not match", "error");
      return;
    }
    if (newPassword.length < 6) {
      showNotification("Password must be at least 6 characters", "error");
      return;
    }
    resetMutation.mutate({ email: resetEmail, code: resetCode, newPassword });
  };

  return (
    <div className="login-root">
      <NotificationToast />

      {/* ================= LEFT — BRAND PANEL ================= */}
      <aside className="le-brand">
        <div aria-hidden="true" className="le-grid" />

        <div className="le-brand-top-row">
          <a href="/" className="le-wordmark" aria-label="Book and Go">
            <span className="w">Book</span>
            <span className="amp">&amp;</span>
            <span className="w">Go</span>
          </a>

          {/* Version pill — visible only on mobile via CSS */}
          <span className="le-version-pill">
            <span className="dot" />
            v3.4
          </span>
        </div>

        <div className="le-hero">
          <div className="le-mono-eyebrow">The back office for any venue</div>
          <h2 className="le-headline">
            Every <span className="serif">booking,</span>
            <br />
            one&nbsp;<span className="le-yellow-tag">operator.</span>
          </h2>
        </div>

        <div className="le-schedule" aria-hidden="true">
          <div className="le-schedule-head">
            <div className="left">
              <span className="dot" />
              Today · Mon 23 May
            </div>
            <div className="count">48 sessions</div>
          </div>
          <div className="le-rows">
            {SCHEDULE_ROWS.map((row) => (
              <div className="le-row" key={row.time}>
                <span className="time">{row.time}</span>
                <div className="middle">
                  <span className={`le-pill ${row.type}`}>{row.kind}</span>
                  <span className="name">{row.name}</span>
                </div>
                <span className="status">{row.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="le-brand-caption">v3.4 · Operator Portal</div>
      </aside>

      {/* ================= RIGHT — FORM PANEL ================= */}
      <div className="le-form-panel">
        <div className="le-help">
          <span>Need help?&nbsp;</span>
          <a href="mailto:support@bookandgo.app">support@bookandgo.app</a>
        </div>

        <div className="le-form-inner">
          {view === "login" && (
            <form autoComplete="on" onSubmit={handleSubmit}>
              <div className="le-form-eyebrow">Operator Portal</div>
              <h1 className="le-form-heading">
                Welcome <span className="serif">back.</span>
              </h1>
              <p className="le-form-sub">
                Sign in to manage today's schedule, your members, and your
                venues — all in one place.
              </p>

              <LeField
                id="login-email"
                label="Email"
                value={email}
                onChange={setEmail}
                icon={<MailIcon />}
                placeholder="you@venue.app"
                type="email"
                autoComplete="email"
              />

              <LeField
                id="login-password"
                label="Password"
                value={password}
                onChange={setPassword}
                icon={<LockIcon />}
                password
                masked={isMasked}
                onToggleMask={() => setIsMasked(!isMasked)}
                placeholder="••••••••"
                autoComplete="current-password"
                trailing={
                  <button
                    type="button"
                    className="le-field-trailing"
                    onClick={() => {
                      setResetEmail(email);
                      setView("forgot");
                    }}
                  >
                    Forgot?
                  </button>
                }
              />

              <div className="le-remember-row">
                <label className="le-check">
                  <input
                    type="checkbox"
                    checked={keepSignedIn}
                    onChange={(e) => setKeepSignedIn(e.target.checked)}
                  />
                  <span className="le-check-box">
                    <CheckIcon />
                  </span>
                  Keep me signed in
                </label>
              </div>

              <button
                type="submit"
                className="le-signin"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? "Signing in…" : "Sign in"}
                {!loginMutation.isPending && <ArrowRightIcon />}
              </button>
            </form>
          )}

          {view === "link" && linkStatus === "checking" && (
            <div>
              <div className="le-form-eyebrow">Set Password</div>
              <p className="le-form-sub">Checking your link…</p>
            </div>
          )}

          {view === "link" && linkStatus === "invalid" && (
            <div>
              <div className="le-form-eyebrow">Set Password</div>
              <h1 className="le-form-heading">
                Link <span className="serif">expired.</span>
              </h1>
              <p className="le-form-sub">{linkError}</p>
              <button type="button" className="le-back-link" onClick={backToSignIn}>
                <ArrowLeftIcon /> Back to sign in
              </button>
            </div>
          )}

          {view === "link" && linkStatus === "valid" && (
            <form autoComplete="off" onSubmit={handleLinkSubmit}>
              <div className="le-form-eyebrow">Set Password</div>
              <h1 className="le-form-heading">
                New <span className="serif">password.</span>
              </h1>
              <p className="le-form-sub">
                Set a new password for <strong>{linkInfo?.email}</strong>.
              </p>

              <LeField
                id="link-new-password"
                label="New password"
                value={newPassword}
                onChange={setNewPassword}
                icon={<LockIcon />}
                password
                masked={newPasswordMasked}
                onToggleMask={() => setNewPasswordMasked(!newPasswordMasked)}
                placeholder="Enter new password"
                autoComplete="new-password"
                autoFocus
              />

              <LeField
                id="link-confirm-password"
                label="Confirm password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                icon={<LockIcon />}
                password
                masked={confirmPasswordMasked}
                onToggleMask={() =>
                  setConfirmPasswordMasked(!confirmPasswordMasked)
                }
                placeholder="Confirm new password"
                autoComplete="new-password"
              />

              <button
                type="submit"
                className="le-signin"
                disabled={completeLinkMutation.isPending}
              >
                {completeLinkMutation.isPending ? "Saving…" : "Set password"}
                {!completeLinkMutation.isPending && <ArrowRightIcon />}
              </button>

              <button type="button" className="le-back-link" onClick={backToSignIn}>
                <ArrowLeftIcon /> Back to sign in
              </button>
            </form>
          )}

          {view === "forgot" && (
            <form autoComplete="off" onSubmit={handleForgotSubmit}>
              <div className="le-form-eyebrow">Reset Password</div>
              <h1 className="le-form-heading">
                Forgot your <span className="serif">password?</span>
              </h1>
              <p className="le-form-sub">
                Enter your email and we'll send you a 6-digit code to reset it.
              </p>

              <LeField
                id="forgot-email"
                label="Email"
                value={resetEmail}
                onChange={setResetEmail}
                icon={<MailIcon />}
                placeholder="you@venue.app"
                type="email"
                autoFocus
              />

              <button
                type="submit"
                className="le-signin"
                disabled={forgotMutation.isPending}
              >
                {forgotMutation.isPending ? "Sending…" : "Send reset code"}
                {!forgotMutation.isPending && <ArrowRightIcon />}
              </button>

              <button
                type="button"
                className="le-back-link"
                onClick={clearResetState}
              >
                <ArrowLeftIcon /> Back to sign in
              </button>
            </form>
          )}

          {view === "reset" && (
            <form autoComplete="off" onSubmit={handleResetSubmit}>
              <div className="le-form-eyebrow">Reset Password</div>
              <h1 className="le-form-heading">
                New <span className="serif">password.</span>
              </h1>
              <p className="le-form-sub">
                Enter the code we sent to <strong>{resetEmail}</strong> and
                choose a new password.
              </p>

              <LeField
                id="reset-code"
                label="Verification code"
                value={resetCode}
                onChange={(v) => setResetCode(v.replace(/\D/g, "").slice(0, 6))}
                icon={<LockIcon />}
                placeholder="000000"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                inputStyle={{
                  letterSpacing: "8px",
                  fontSize: "20px",
                  textAlign: "center",
                }}
              />

              <LeField
                id="reset-new-password"
                label="New password"
                value={newPassword}
                onChange={setNewPassword}
                icon={<LockIcon />}
                password
                masked={newPasswordMasked}
                onToggleMask={() => setNewPasswordMasked(!newPasswordMasked)}
                placeholder="Enter new password"
              />

              <LeField
                id="reset-confirm-password"
                label="Confirm password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                icon={<LockIcon />}
                password
                masked={confirmPasswordMasked}
                onToggleMask={() =>
                  setConfirmPasswordMasked(!confirmPasswordMasked)
                }
                placeholder="Confirm new password"
              />

              <button
                type="submit"
                className="le-signin"
                disabled={resetMutation.isPending || resetCode.length !== 6}
              >
                {resetMutation.isPending ? "Resetting…" : "Reset password"}
                {!resetMutation.isPending && <ArrowRightIcon />}
              </button>

              <button
                type="button"
                className="le-back-link"
                onClick={clearResetState}
              >
                <ArrowLeftIcon /> Back to sign in
              </button>
            </form>
          )}
        </div>

        <div className="le-form-footer">
          <span>Book &amp; Go · v3.4.1</span>
          <span>
            <a href="/privacy">Privacy</a>
            <span aria-hidden="true" style={{ margin: "0 6px", opacity: 0.5 }}>·</span>
            <a href="/terms">Terms</a>
          </span>
        </div>
      </div>
    </div>
  );
}
